import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useHelpArticle } from "@/hooks/useHelpCenter";
import { FileText, Calendar, Tag } from "lucide-react";

export function HelpArticlePage() {
  const { categorySlug, articleSlug } = useParams<{
    categorySlug: string;
    articleSlug: string;
  }>();
  const { data: article, isLoading, error } = useHelpArticle(
    categorySlug || "",
    articleSlug || ""
  );

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Skeleton className="h-6 w-48 mb-6" />
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-4 w-32 mb-8" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <h2 className="text-lg font-medium mb-2">Article Not Found</h2>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/help"
              className="text-primary hover:underline mt-4 inline-block"
            >
              Return to Help Center
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const category = article.category;
  const tags = (article as any).tags || [];

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/help">Help Center</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {category && (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/help/${category.slug}`}>{category.name}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </>
          )}
          <BreadcrumbItem>
            <BreadcrumbPage>{article.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <article>
        <header className="mb-8">
          <h1 className="text-3xl font-bold mb-4">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Updated {new Date(article.updated_at).toLocaleDateString()}
            </span>
            {tags.length > 0 && (
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                <div className="flex gap-1">
                  {tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="prose prose-gray dark:prose-invert max-w-none">
          <ArticleContent content={article.content} />
        </div>
      </article>
    </div>
  );
}

export function ArticleContent({ content }: { content: string }) {
  // Enhanced markdown-like rendering
  // Converts markdown patterns to HTML elements including tables and images
  const lines = content.split("\n");
  const elements: JSX.Element[] = [];
  let currentParagraph: string[] = [];
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeaders: string[] = [];
  // Consecutive list items are wrapped in one <ul>/<ol>, so numbering restarts per list
  // instead of counting every <li> on the page.
  let listItems: JSX.Element[] = [];
  let listType: "ul" | "ol" | null = null;
  let listStart = 1;

  const flushList = () => {
    if (listType === "ol") {
      elements.push(<ol key={elements.length} start={listStart} className="mb-4">{listItems}</ol>);
    } else if (listType === "ul") {
      elements.push(<ul key={elements.length} className="mb-4">{listItems}</ul>);
    }
    listItems = [];
    listType = null;
  };

  const pushListItem = (type: "ul" | "ol", text: string, start = 1) => {
    if (listType !== type) {
      flushList();
      listType = type;
      listStart = start;
    }
    listItems.push(
      <li key={listItems.length} className={`ml-6 ${type === "ul" ? "list-disc" : "list-decimal"} mb-1`}>
        {formatInlineText(text)}
      </li>
    );
  };

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(" ");
      elements.push(
        <p key={elements.length} className="mb-4 leading-relaxed">
          {formatInlineText(text)}
        </p>
      );
      currentParagraph = [];
    }
  };

  const flushTable = () => {
    if (tableHeaders.length > 0 || tableRows.length > 0) {
      elements.push(
        <div key={elements.length} className="overflow-x-auto mb-4">
          <table className="min-w-full border-collapse border border-border">
            {tableHeaders.length > 0 && (
              <thead>
                <tr className="bg-muted">
                  {tableHeaders.map((header, i) => (
                    <th
                      key={i}
                      className="border border-border px-4 py-2 text-left font-semibold"
                    >
                      {formatInlineText(header.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {tableRows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? "" : "bg-muted/50"}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border border-border px-4 py-2"
                    >
                      {formatInlineText(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableHeaders = [];
      tableRows = [];
      inTable = false;
    }
  };

  const parseTableRow = (line: string): string[] => {
    // Remove leading/trailing pipes and split by pipe
    return line
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => cell.trim());
  };

  const isTableSeparator = (line: string): boolean => {
    // Matches lines like |---|---|---| or |:---|:---:|---:|
    return /^\|?[\s:-]+\|[\s:|:-]+\|?$/.test(line);
  };

  lines.forEach((line) => {
    // Any non-list, non-blank line ends the open list (a blank line inside a list doesn't).
    if (!inCodeBlock && line.trim() !== "" && !/^([-*]|\d+\.)\s/.test(line)) {
      flushList();
    }

    // Code blocks
    if (line.startsWith("```")) {
      if (inCodeBlock) {
        elements.push(
          <pre
            key={elements.length}
            className="bg-muted p-4 rounded-lg overflow-x-auto mb-4 text-sm"
          >
            <code>{codeBlockContent.join("\n")}</code>
          </pre>
        );
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        flushParagraph();
        flushTable();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    // Table handling
    if (line.includes("|") && line.trim().startsWith("|")) {
      flushParagraph();

      if (isTableSeparator(line)) {
        // This is the separator row, skip it but mark we're in a table
        inTable = true;
        return;
      }

      const cells = parseTableRow(line);

      if (!inTable && tableHeaders.length === 0) {
        // First row is headers
        tableHeaders = cells;
      } else {
        // Data row
        tableRows.push(cells);
        inTable = true;
      }
      return;
    } else if (inTable) {
      // Line doesn't contain table syntax, flush the table
      flushTable();
    }

    // Image handling: ![alt](url)
    const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      flushParagraph();
      elements.push(
        <figure key={elements.length} className="my-6">
          <img
            src={imageMatch[2]}
            alt={imageMatch[1]}
            className="rounded-lg border border-border max-w-full h-auto"
          />
          {imageMatch[1] && (
            <figcaption className="text-sm text-muted-foreground mt-2 text-center">
              {imageMatch[1]}
            </figcaption>
          )}
        </figure>
      );
      return;
    }

    // Headers
    if (line.startsWith("### ")) {
      flushParagraph();
      flushTable();
      elements.push(
        <h3 key={elements.length} className="text-lg font-semibold mt-6 mb-3">
          {line.slice(4)}
        </h3>
      );
      return;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushTable();
      elements.push(
        <h2 key={elements.length} className="text-xl font-semibold mt-8 mb-4">
          {line.slice(3)}
        </h2>
      );
      return;
    }
    if (line.startsWith("# ")) {
      flushParagraph();
      flushTable();
      elements.push(
        <h1 key={elements.length} className="text-2xl font-bold mt-8 mb-4">
          {line.slice(2)}
        </h1>
      );
      return;
    }

    // Horizontal rule
    if (line.match(/^---+$/)) {
      flushParagraph();
      flushTable();
      elements.push(<hr key={elements.length} className="my-6 border-border" />);
      return;
    }

    // Unordered list items
    if (line.match(/^[-*]\s/)) {
      flushParagraph();
      flushTable();
      pushListItem("ul", line.slice(2));
      return;
    }

    // Ordered list items
    if (line.match(/^\d+\.\s/)) {
      flushParagraph();
      flushTable();
      pushListItem("ol", line.replace(/^\d+\.\s/, ""), parseInt(line, 10));
      return;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      flushParagraph();
      flushTable();
      elements.push(
        <blockquote
          key={elements.length}
          className="border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground my-4"
        >
          {formatInlineText(line.slice(2))}
        </blockquote>
      );
      return;
    }

    // Empty line - end paragraph
    if (line.trim() === "") {
      flushParagraph();
      flushTable();
      return;
    }

    // Regular text - add to current paragraph
    currentParagraph.push(line);
  });

  flushParagraph();
  flushTable();
  flushList();

  return <>{elements}</>;
}

function formatInlineText(text: string): React.ReactNode {
  // Handle inline formatting: bold, italic, code, links
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Inline code
    const codeMatch = remaining.match(/`([^`]+)`/);
    if (codeMatch && codeMatch.index !== undefined) {
      if (codeMatch.index > 0) {
        parts.push(remaining.slice(0, codeMatch.index));
      }
      parts.push(
        <code
          key={key++}
          className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono"
        >
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch.index + codeMatch[0].length);
      continue;
    }

    // Bold
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    if (boldMatch && boldMatch.index !== undefined) {
      if (boldMatch.index > 0) {
        parts.push(remaining.slice(0, boldMatch.index));
      }
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }

    // Italic
    const italicMatch = remaining.match(/\*([^*]+)\*/);
    if (italicMatch && italicMatch.index !== undefined) {
      if (italicMatch.index > 0) {
        parts.push(remaining.slice(0, italicMatch.index));
      }
      parts.push(<em key={key++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      continue;
    }

    // Links
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch && linkMatch.index !== undefined) {
      if (linkMatch.index > 0) {
        parts.push(remaining.slice(0, linkMatch.index));
      }
      parts.push(
        <a
          key={key++}
          href={linkMatch[2]}
          className="text-primary hover:underline"
          target={linkMatch[2].startsWith("http") ? "_blank" : undefined}
          rel={linkMatch[2].startsWith("http") ? "noopener noreferrer" : undefined}
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.slice(linkMatch.index + linkMatch[0].length);
      continue;
    }

    // No more matches - add remaining text
    parts.push(remaining);
    break;
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}
